import { useEffect, useState } from 'react'

export interface SlackChannelChoice { id: string; name: string }

export function useProjectSlackChannels(projectId: string, active: boolean) {
  const [channels, setChannels] = useState<SlackChannelChoice[]>([])
  const [request, setRequest] = useState({ cursor: '', revision: 0 })
  const [nextCursor, setNextCursor] = useState('')
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (!active) return
    const abort = new AbortController()
    setLoading(true)
    setFailed(false)
    // Read-only external availability query; all mutations remain Inertia.
    // The keyed project card and abort guard discard late old-project results.
    const query = new URLSearchParams({ project_id: projectId, cursor: request.cursor })
    fetch(`/dashboard/discovery/project_slack_channel?${query}`, {
      signal: abort.signal, cache: 'no-store', headers: { Accept: 'application/json' },
    }).then(async response => {
      if (!response.ok) throw new Error('channel lookup failed')
      return response.json() as Promise<{ channels: SlackChannelChoice[]; next_cursor: string }>
    }).then(result => {
      if (abort.signal.aborted) return
      setChannels(previous => Array.from(new Map([
        ...(request.cursor ? previous : []), ...result.channels,
      ].map(channel => [channel.id, channel])).values()))
      setNextCursor(result.next_cursor)
    }).catch(() => { if (!abort.signal.aborted) setFailed(true) })
      .finally(() => { if (!abort.signal.aborted) setLoading(false) })
    return () => abort.abort()
  }, [projectId, active, request])
  const refresh = () => {
    setChannels([])
    setNextCursor('')
    setRequest(previous => ({ cursor: '', revision: previous.revision + 1 }))
  }
  const more = () => setRequest(previous => ({ cursor: nextCursor, revision: previous.revision + 1 }))
  return { channels, loading, failed, nextCursor, refresh, more }
}
